import React, { useState, useEffect, memo, useMemo, useCallback } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { prefectures } from "./data";

const AkiyaAkiya = "/images/prefecture/AkiyaAkiya.png";
const AgentAkiya = "/images/prefecture/AgentAkiya.png";

// Pre-create icon objects to avoid recreation on every render
const createMarkerIcons = () => {
  const baseIcon = { url: AkiyaAkiya };
  const hoveredIcon = { url: AkiyaAkiya };
  
  // Use AgentAkiya PNG for agent markers
  const agentIcon = { url: AgentAkiya };
  const agentHoveredIcon = { url: AgentAkiya };

  return { baseIcon, hoveredIcon, agentIcon, agentHoveredIcon };
};

const CustomMarker = memo(
  ({ position, title, id, isHovered, hasAgent, handleMarkerClick, handleMouseOver, handleMouseOut }) => {
    const markerIcon = useMemo(() => {
      // Only create icons when Google Maps is loaded
      if (!window.google?.maps) return null;
      
      const icons = createMarkerIcons();
      
      if (hasAgent) {
        // Use AgentAkiya PNG with appropriate scaling
        const scaledSize = isHovered 
          ? new window.google.maps.Size(50, 50) 
          : new window.google.maps.Size(40, 40);
        
        return { url: AgentAkiya, scaledSize };
      }
      
      // For regular akiya markers, use AkiyaAkiya PNG
      const scaledSize = isHovered 
        ? new window.google.maps.Size(50, 50) 
        : new window.google.maps.Size(40, 40);
      
      return { url: AkiyaAkiya, scaledSize };
    }, [isHovered, hasAgent]);

    // Memoize event handlers to prevent unnecessary re-renders
    const onMarkerClick = useCallback(() => handleMarkerClick(id), [handleMarkerClick, id]);
    const onMouseOver = useCallback(() => handleMouseOver(id), [handleMouseOver, id]);

    if (!markerIcon) return null;

    return (
      <Marker
        position={position}
        title={title}
        icon={markerIcon}
        onClick={onMarkerClick}
        onMouseOver={onMouseOver}
        onMouseOut={handleMouseOut}
      />
    );
  }
);

const MapComponent = ({ prefectureName, apiKey, akiyaData }) => {
  const [markers, setMarkers] = useState([]);
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [initialCenter, setInitialCenter] = useState(null);

  // Memoize prefecture lookup to avoid repeated searches
  const prefectureCoordinates = useMemo(() => {
    const found = prefectures.find(p => p.name === prefectureName);
    return found ? { lat: found.coordinates[0], lng: found.coordinates[1] } : null;
  }, [prefectureName]);

  // Memoize processed markers to avoid recreation on every render
  const processedMarkers = useMemo(() => {
    if (!akiyaData || !Array.isArray(akiyaData)) return [];
    
    return akiyaData.map((item) => ({
      position: { lat: item.Coordinates[0], lng: item.Coordinates[1] },
      title: item.Address,
      id: item.property_id,
      hasAgent: item.agentId !== undefined && item.agentId !== null && item.agentId !== '',
    }));
  }, [akiyaData]);

  useEffect(() => {
    if (prefectureCoordinates) {
      setInitialCenter(prefectureCoordinates);
    } else {
      console.error(`Coordinates not found for ${prefectureName}`);
    }
  }, [prefectureCoordinates, prefectureName]);

  useEffect(() => {
    setMarkers(processedMarkers);
  }, [processedMarkers]);

  // Memoize event handlers to prevent child re-renders
  const handleMarkerClick = useCallback((id) => {
    const url = "/individual-akiya/" + id;
    window.open(url, "_blank");
  }, []);

  const handleMouseOver = useCallback((id) => {
    setHoveredMarkerId(id);
  }, []);

  const handleMouseOut = useCallback(() => {
    setHoveredMarkerId(null);
  }, []);

  // Memoize container style to prevent recreation
  const containerStyle = useMemo(() => ({
    width: "100%",
    height: "490px",
  }), []);

  // Memoize map options for better performance
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: true,
    // Optimize rendering
    optimized: true,
    // Disable animations for better performance with many markers
    gestureHandling: 'greedy',
  }), []);

  if (!initialCenter) {
    return (
      <div style={containerStyle} className="flex items-center justify-center bg-gray-100">
        <div className="text-gray-600">Loading map...</div>
      </div>
    );
  }

  return (
    <LoadScript 
      googleMapsApiKey={apiKey}
      // Load only necessary libraries
      libraries={[]}
      // Prevent multiple loads
      preventGoogleFontsLoading={true}
    >
      <GoogleMap 
        mapContainerStyle={containerStyle} 
        zoom={9} 
        center={initialCenter}
        options={mapOptions}
      >
        {markers.map((marker) => (
          <CustomMarker
            key={marker.id}
            position={marker.position}
            title={marker.title}
            id={marker.id}
            isHovered={hoveredMarkerId === marker.id}
            hasAgent={marker.hasAgent}
            handleMarkerClick={handleMarkerClick}
            handleMouseOver={handleMouseOver}
            handleMouseOut={handleMouseOut}
          />
        ))}
      </GoogleMap>
    </LoadScript>
  );
};

export default MapComponent;